import axios from "axios"

export interface AddCartItemInput {
  productId: number
  quantity: number
  variationId?: number
  attributes?: Record<string, string>
}

export interface AddCartItemResponse {
  ok: boolean
  cartCount: number
  notices: string[]
  fragments: Record<string, string>
  message?: string
}

export interface CartItem {
  id: string
  key: string
  productId: number
  name: string
  slug?: string | null
  stockStatus?: "instock" | "outofstock" | "onbackorder" | null
  store: string
  quantity: number
  price: number
  lineTotal: number
  thumbnail?: string
}

export interface CartCoupon {
  code: string
  discountTotal: number
}

export interface CartShippingAddress {
  country: string
  state: string
  city: string
  postcode: string
}

export interface CartSnapshot {
  ok: boolean
  cartCount: number
  items: CartItem[]
  totals: {
    subtotal: number
    discountTotal: number
    total: number
    currency: string
  }
  coupons: CartCoupon[]
  shippingAddress?: CartShippingAddress
}

export interface UpdateCartItemQuantityInput {
  key: string
  quantity: number
}

export interface RemoveCartItemInput {
  key: string
}

export interface CartCouponInput {
  code: string
}

export interface CartShippingAddressInput {
  country: string
  state: string
  city: string
  postcode: string
}

export interface CartMutationResponse {
  ok: boolean
  message?: string
}

export interface CartShippingAddressResponse {
  ok: boolean
  message?: string
}

export const addCartItem = async ({
  productId,
  quantity,
  variationId,
  attributes,
}: AddCartItemInput): Promise<AddCartItemResponse> => {
  const { data } = await axios.post<AddCartItemResponse>("/api/cart/items", {
    productId,
    quantity,
    variationId,
    attributes,
  })

  return data
}

export const getCartItems = async (): Promise<CartSnapshot> => {
  const { data } = await axios.get<CartSnapshot>("/api/cart/items")
  return data
}

export const updateCartItemQuantity = async ({
  key,
  quantity,
}: UpdateCartItemQuantityInput): Promise<CartMutationResponse> => {
  const { data } = await axios.patch<CartMutationResponse>("/api/cart/items", {
    key,
    quantity,
  })

  return data
}

export const removeCartItem = async ({
  key,
}: RemoveCartItemInput): Promise<CartMutationResponse> => {
  const { data } = await axios.delete<CartMutationResponse>("/api/cart/items", {
    data: {
      key,
    },
  })

  return data
}

export const applyCartCoupon = async ({
  code,
}: CartCouponInput): Promise<CartMutationResponse> => {
  const { data } = await axios.post<CartMutationResponse>("/api/cart/coupons", {
    code,
  })

  return data
}

export const removeCartCoupon = async ({
  code,
}: CartCouponInput): Promise<CartMutationResponse> => {
  const { data } = await axios.delete<CartMutationResponse>(
    "/api/cart/coupons",
    {
      data: {
        code,
      },
    }
  )

  return data
}

export const updateCartShippingAddress = async (
  input: CartShippingAddressInput
): Promise<CartShippingAddressResponse> => {
  const { data } = await axios.post<CartShippingAddressResponse>(
    "/api/cart/address",
    input
  )

  return data
}
